import { runGraphQL } from "../nr1/nerdgraph";

/**
 * Entity lookups for the standalone entity panel.
 *
 * Inside New Relic, clicking a store or service opens the platform's stacked
 * entity view. That view cannot be reproduced by embedding: one.newrelic.com
 * sends `X-Frame-Options: DENY`, so an iframe is impossible. Instead the panel
 * reads the same entity through NerdGraph and renders it natively.
 */

const ENTITY_QUERY = `
  query EntityDetail($guid: EntityGuid!) {
    actor {
      entity(guid: $guid) {
        guid
        name
        entityType
        domain
        type
        accountId
        reporting
        alertSeverity
        tags { key values }
        ... on AlertableEntity {
          recentAlertViolations(count: 5) {
            violationId
            label
            level
            openedAt
            closedAt
            violationUrl
          }
        }
      }
    }
  }
`;

/**
 * A New Relic entity GUID is base64 of `accountId|DOMAIN|TYPE|identifier`.
 * Decoding it locally means the panel can show meaningful context instantly —
 * and still show something useful if the lookup fails or the entity has been
 * removed.
 */
export function decodeEntityGuid(guid) {
  if (!guid || typeof guid !== "string") return null;
  try {
    const padded = guid + "=".repeat((4 - (guid.length % 4)) % 4);
    const decoded = atob(padded);
    const [accountId, domain, type, ...rest] = decoded.split("|");
    if (!accountId || !domain) return null;
    return {
      accountId,
      domain,
      type: type || null,
      identifier: rest.join("|") || null,
    };
  } catch {
    return null;
  }
}

const NR_BASE = { US: "https://one.newrelic.com", EU: "https://one.eu.newrelic.com" };

export function entityUrl(guid, region = "US") {
  return `${NR_BASE[region] || NR_BASE.US}/redirect/entity/${encodeURIComponent(guid)}`;
}

/** @returns {Promise<object|null>} null when the entity does not resolve. */
export async function fetchEntity(guid, { signal } = {}) {
  const data = await runGraphQL({
    query: ENTITY_QUERY,
    variables: { guid },
    signal,
  });
  return data?.actor?.entity ?? null;
}
